/**
 * DataGridCellText — Text input cell variant
 *
 * Renders a text input that auto-focuses and selects all text on mount.
 * Commits on blur or Enter. Cancels on Escape.
 */

import { useRef, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { DataGridCellVariantProps } from './types';

export function DataGridCellText({
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
}: DataGridCellVariantProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(String(value ?? ''));

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
      onStartEditing();
    }
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter') {
      e.stopPropagation();
      onCommit(localValue);
    } else if (e.key === 'Tab') {
      // Let Tab bubble to the grid keyboard handler
      onCommit(localValue);
    } else {
      // Stop propagation for all other keys (arrows, letters)
      // so the grid keyboard handler doesn't intercept them
      e.stopPropagation();
    }
  };

  return (
    <div className="relative flex items-center w-full h-full">
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onCommit(localValue)}
        onKeyDown={handleKeyDown}
        className="w-full h-full bg-transparent text-sm outline-none border-none px-1"
        aria-label="Edit cell"
      />
      {saveState.status === 'saving' && (
        <Loader2 className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground animate-spin" />
      )}
    </div>
  );
}
